/**
 * 「作業係の仕事」のカードの中身（ADR-0049、D7 の (a)）。DOM を持たない部分（テストできるように分ける）。
 * 何を出すかは Gateway が DB の行からコードで作った値（GET /v1/jobs）だけで決める。
 */
export interface JobCardItem {
  job_id: string;
  title: string;
  kind: string;
  status: "queued" | "running" | "paused" | "done" | "failed" | "cancelled";
  status_label: string;
  stop_requested: boolean;
  created_at: string;
  finished_at: string | null;
  last_event: { at: string; summary: string } | null;
  branch: string | null;
  result: string | null;
}

export interface JobsView {
  jobs: JobCardItem[];
  worker: { state: string; seen_ago_sec: number; stale: boolean } | null;
  window: string;
  refresh_sec: number;
}

const ACTIVE = new Set<JobCardItem["status"]>(["queued", "running", "paused"]);

export function isActive(j: JobCardItem): boolean {
  return ACTIVE.has(j.status);
}

/** 取り消しのボタンを出すか（まだ終わっていなくて、取り消しを頼んでいない） */
export function canCancel(j: JobCardItem): boolean {
  return isActive(j) && !j.stop_requested;
}

function ago(sec: number): string {
  if (sec < 90) return "さっき";
  if (sec < 3600) return `${Math.round(sec / 60)} 分前`;
  if (sec < 86_400) return `${Math.round(sec / 3600)} 時間前`;
  return `${Math.round(sec / 86_400)} 日前`;
}

/** 作業係の生存の 1 行（正は時刻。Gateway が seen_at と今の差を返す） */
export function workerLine(v: JobsView): string {
  if (!v.worker) return `作業係はまだ見えていません（動く時間帯: ${v.window}）`;
  const when = ago(v.worker.seen_ago_sec);
  if (v.worker.stale)
    return `作業係は ${when}から見えていません（眠っているか止まっています。動く時間帯: ${v.window}）`;
  const state: Record<string, string> = {
    idle: "待機中",
    running: "仕事中",
    outside_window: "時間帯の外で待機中",
    frozen: "凍結中で止まっています",
    stopping: "止めています",
  };
  const checked = when === "さっき" ? "さっき確認" : `${when}に確認`;
  return `作業係: ${state[v.worker.state] ?? v.worker.state}（${checked}。動く時間帯: ${v.window}）`;
}

export interface JobsCardModel {
  show: boolean;
  active: JobCardItem[];
  finished: JobCardItem[];
  worker: string;
  /** 開いている間に描き直す間隔（ミリ秒）。動いている仕事が無ければ null（描き直さない） */
  refreshMs: number | null;
}

export function jobsCardModel(v: JobsView): JobsCardModel {
  const active = v.jobs.filter(isActive);
  const finished = v.jobs.filter((j) => !isActive(j));
  return {
    show: v.jobs.length > 0,
    active,
    finished,
    worker: workerLine(v),
    refreshMs: active.length > 0 ? Math.max(2, v.refresh_sec) * 1000 : null,
  };
}
